import { Deferred, router, usePage } from "@inertiajs/react";
import type { Table as TableTanstack } from "@tanstack/react-table";
import { useLocalStorage } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronsUpDown, ListFilterIcon, Search, X } from "lucide-react";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { groupLocationsByCountry } from "@/lib/locations";
import { cn, debounce, populateQueryParams } from "@/lib/utils";
import type { PatientIndexGlobalPageProps } from "@/pages/AdminPortal/Patient/Index";
import type { Patient } from "@/types/admin-portal/patient";

type TableToolbarDataProps = TableTanstack<Patient>;

export interface ToolbarTableProps {
	table: TableToolbarDataProps;
}

const PREFERENCES_STORAGE_KEY = "admin-portal-patient-preferences";

const ToolbarTable = memo(function ToolbarTable({
	table: _,
}: ToolbarTableProps) {
	const { t: tpf } = useTranslation("patients", { keyPrefix: "filter" });
	const { url: pageURL, props: globalProps } =
		usePage<PatientIndexGlobalPageProps>();
	const isMobile = useIsMobile();

	// ── Show/hide filter container preference ─────────────────────────────
	const [preferences, savePreferences] = useLocalStorage(
		PREFERENCES_STORAGE_KEY,
		{ isShowFilter: false },
	);
	const handleToggleFilter = useCallback(() => {
		savePreferences((prev) => ({ ...prev, isShowFilter: !prev.isShowFilter }));
	}, [savePreferences]);

	const locations = useMemo(
		() => globalProps?.filterOptions?.locations,
		[globalProps?.filterOptions?.locations],
	);
	const groupedLocations = useMemo(
		() => groupLocationsByCountry(globalProps?.filterOptions?.locations || []),
		[globalProps?.filterOptions?.locations],
	);
	const [isSearching, setIsSearching] = useState(false);
	const [search, setSearch] = useState<{
		text: string;
		city: string;
		isVerified: string;
		registrationSource: string;
		gender: string;
		emailExist: string;
		phoneExist: string;
		accountStatus: string;
	}>(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		const text = currentQuery?.text || "";
		const city = currentQuery?.city || "";
		const isVerified = currentQuery?.isVerified || "";
		const registrationSource = currentQuery?.registrationSource || "";
		const gender = currentQuery?.gender || "";
		const emailExist = currentQuery?.emailExist || "";
		const phoneExist = currentQuery?.phoneExist || "";
		const accountStatus = currentQuery?.accountStatus || "";

		return {
			text,
			city,
			isVerified,
			registrationSource,
			gender,
			emailExist,
			phoneExist,
			accountStatus,
		};
	});
	const updateQueryParams = useCallback(
		debounce((url) => {
			router.get(
				url,
				{},
				{
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "patients"],
					onStart: () => {
						setIsSearching(true);
					},
					onFinish: () => {
						setTimeout(() => {
							setIsSearching(false);
						}, 250);
					},
				},
			);
		}, 250),
		[],
	);
	const handleFilterBy = useCallback(
		({ value, type }: { value: string; type: keyof typeof search }) => {
			const nextFilters = { ...search, [type]: value };
			setSearch(nextFilters);

			// Only send the changed field, updateQueryParams will merge and clean status if needed
			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...nextFilters,
					// null value means reset the another param value
					page: null,
					limit: null,
				}),
			);
			updateQueryParams(fullUrl);
		},
		[search, pageURL, updateQueryParams],
	);
	const handleResetAllFilters = useCallback(() => {
		const reset = {
			text: "",
			city: "",
			isVerified: "",
			registrationSource: "",
			gender: "",
			emailExist: "",
			phoneExist: "",
			accountStatus: "",
		};
		setSearch(reset);

		const { fullUrl } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({ ...reset, page: null, limit: null }),
		);
		updateQueryParams(fullUrl);
	}, [pageURL, updateQueryParams]);

	// ── Active filter chips ───────────────────────────────────────────────
	const activeFilters = useMemo(() => {
		const result: {
			key: keyof typeof search;
			label: string;
			value: string;
		}[] = [];
		if (search.text) {
			result.push({
				key: "text",
				label: tpf("chip.search"),
				value: search.text,
			});
		}
		if (search.city) {
			result.push({
				key: "city",
				label: tpf("chip.region"),
				value: search.city,
			});
		}
		if (search.isVerified) {
			result.push({
				key: "isVerified",
				label: tpf("chip.verified"),
				value: tpf(`verified.items.${search.isVerified}`),
			});
		}
		if (search.registrationSource) {
			result.push({
				key: "registrationSource",
				label: tpf("chip.registration_source"),
				value: search.registrationSource.replaceAll("_", " "),
			});
		}
		if (search.gender) {
			result.push({
				key: "gender",
				label: tpf("chip.gender"),
				value: tpf(`gender.items.${search.gender.toLowerCase()}`),
			});
		}
		if (search.emailExist) {
			result.push({
				key: "emailExist",
				label: tpf("chip.email_exist"),
				value: tpf(`email_exist.items.${search.emailExist}`),
			});
		}
		if (search.phoneExist) {
			result.push({
				key: "phoneExist",
				label: tpf("chip.phone_exist"),
				value: tpf(`phone_exist.items.${search.phoneExist}`),
			});
		}
		if (search.accountStatus) {
			result.push({
				key: "accountStatus",
				label: tpf("chip.account_status"),
				value: tpf(`account_status.items.${search.accountStatus}`),
			});
		}
		return result;
	}, [search, tpf]);

	return (
		<section className="space-y-4">
			<div className="flex items-center justify-between gap-2">
				<Button
					type="button"
					variant={preferences.isShowFilter ? "default" : "outline"}
					size={isMobile ? "icon" : "default"}
					className="shadow-none"
					onClick={(event) => {
						event.preventDefault();
						handleToggleFilter();
					}}
				>
					<ListFilterIcon className="size-4" />
					{isMobile
						? null
						: preferences.isShowFilter
							? tpf("close")
							: tpf("label")}
					{!preferences.isShowFilter && activeFilters.length > 0 && (
						<Badge variant="accent" className="px-1 rounded-full">
							{activeFilters.length}
						</Badge>
					)}
				</Button>
			</div>

			<AnimatePresence initial={false}>
				{preferences.isShowFilter && (
					<motion.div
						initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
						exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						transition={{ duration: 0.3, ease: "easeOut" }}
						className="p-4 border shadow-inner rounded-xl bg-background"
					>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							<div className="grid gap-2 col-span-full xl:col-span-2">
								<Input
									placeholder={tpf("search.placeholder")}
									className="shadow-inner bg-input"
									value={search.text}
									isLoading={isSearching}
									StartIcon={{ icon: Search }}
									EndIcon={
										search.text
											? {
													isButton: true,
													icon: X,
													handleOnClick: (event) => {
														event.preventDefault();

														handleFilterBy({ type: "text", value: "" });
													},
												}
											: undefined
									}
									onChange={(event) => {
										const value = event.target.value;

										handleFilterBy({ type: "text", value });
									}}
								/>
								<p className="text-[0.8rem] text-pretty text-muted-foreground">
									{tpf("search.description")}
								</p>
							</div>

							<Deferred
								data={["filterOptions"]}
								fallback={<Skeleton className="w-full rounded-md h-9" />}
							>
								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
												!search?.city && "text-muted-foreground",
											)}
										>
											<p className="truncate">
												{search?.city
													? locations?.find(
															(location) => location.city === search?.city,
														)?.city
													: `${tpf("region.placeholder")}...`}
											</p>
											<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="p-0 w-fit"
										align="start"
										side="bottom"
									>
										<Command>
											<CommandInput
												placeholder={`${tpf("region.search.placeholder")}...`}
												className="h-9"
												autoComplete="address-level2"
											/>
											<CommandList>
												<CommandEmpty>
													{tpf("region.search.empty")}
												</CommandEmpty>
												{groupedLocations?.map((location) => (
													<Fragment key={location.country}>
														<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
															{location.country}
														</span>

														{location.states.map((state, stateIndex) => (
															<CommandGroup
																key={state.name}
																heading={state.name}
															>
																{state.cities.map((city) => (
																	<CommandItem
																		key={city.name}
																		value={city.name}
																		onSelect={() =>
																			handleFilterBy({
																				type: "city",
																				value: city.name,
																			})
																		}
																	>
																		<span>{city.name}</span>
																		<Check
																			className={cn(
																				"ml-auto",
																				city.name === search?.city
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																	</CommandItem>
																))}
																{location.states.length !== stateIndex + 1 && (
																	<CommandSeparator className="mt-2" />
																)}
															</CommandGroup>
														))}
													</Fragment>
												))}
											</CommandList>

											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														onSelect={() => {
															handleFilterBy({ type: "city", value: "" });
														}}
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{tpf("region.search.clear")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</Deferred>

							<Select
								value={search.isVerified}
								onValueChange={(value) =>
									handleFilterBy({ type: "isVerified", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.isVerified ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("verified.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="verified">
											{tpf("verified.items.verified").toUpperCase()}
										</SelectItem>
										<SelectItem value="unverified">
											{tpf("verified.items.unverified").toUpperCase()}
										</SelectItem>
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "isVerified", value: "" });
										}}
									>
										{tpf("verified.clear")}
									</Button>
								</SelectContent>
							</Select>

							<Select
								value={search.registrationSource}
								onValueChange={(value) =>
									handleFilterBy({ type: "registrationSource", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.registrationSource ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("registration_source.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{["WEB", "MOBILE_ANDROID", "MOBILE_IOS", "ADMIN_PANEL"].map(
											(source) => (
												<SelectItem key={source} value={source}>
													{source.replaceAll("_", " ")}
												</SelectItem>
											),
										)}
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "registrationSource", value: "" });
										}}
									>
										{tpf("registration_source.clear")}
									</Button>
								</SelectContent>
							</Select>

							<Select
								value={search.gender}
								onValueChange={(value) =>
									handleFilterBy({ type: "gender", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.gender ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("gender.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="MALE">
											{tpf("gender.items.male").toUpperCase()}
										</SelectItem>
										<SelectItem value="FEMALE">
											{tpf("gender.items.female").toUpperCase()}
										</SelectItem>
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "gender", value: "" });
										}}
									>
										{tpf("gender.clear")}
									</Button>
								</SelectContent>
							</Select>

							<Select
								value={search.emailExist}
								onValueChange={(value) =>
									handleFilterBy({ type: "emailExist", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.emailExist ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("email_exist.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="yes">
											{tpf("email_exist.items.yes").toUpperCase()}
										</SelectItem>
										<SelectItem value="no">
											{tpf("email_exist.items.no").toUpperCase()}
										</SelectItem>
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "emailExist", value: "" });
										}}
									>
										{tpf("email_exist.clear")}
									</Button>
								</SelectContent>
							</Select>

							<Select
								value={search.phoneExist}
								onValueChange={(value) =>
									handleFilterBy({ type: "phoneExist", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.phoneExist ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("phone_exist.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="yes">
											{tpf("phone_exist.items.yes").toUpperCase()}
										</SelectItem>
										<SelectItem value="no">
											{tpf("phone_exist.items.no").toUpperCase()}
										</SelectItem>
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "phoneExist", value: "" });
										}}
									>
										{tpf("phone_exist.clear")}
									</Button>
								</SelectContent>
							</Select>

							<Select
								value={search.accountStatus}
								onValueChange={(value) =>
									handleFilterBy({ type: "accountStatus", value })
								}
							>
								<SelectTrigger
									className={cn(
										"bg-input shadow-inner",
										!search.accountStatus ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={`${tpf("account_status.placeholder")}...`}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										<SelectItem value="none">
											{tpf("account_status.items.none").toUpperCase()}
										</SelectItem>
										<SelectItem value="customer">
											{tpf("account_status.items.customer").toUpperCase()}
										</SelectItem>
										<SelectItem value="admin">
											{tpf("account_status.items.admin").toUpperCase()}
										</SelectItem>
										<SelectItem value="therapist">
											{tpf("account_status.items.therapist").toUpperCase()}
										</SelectItem>
									</SelectGroup>
									<SelectSeparator />
									<Button
										className="w-full px-2 font-medium uppercase"
										variant="ghost"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											handleFilterBy({ type: "accountStatus", value: "" });
										}}
									>
										{tpf("account_status.clear")}
									</Button>
								</SelectContent>
							</Select>
						</div>

						{activeFilters.length > 0 && (
							<div className="flex flex-wrap items-center gap-2 mt-6">
								<span className="text-xs font-medium text-muted-foreground">
									{tpf("active_filters")}:
								</span>
								{activeFilters.map((f) => (
									<Badge
										key={f.key}
										variant="secondary"
										className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[10px] uppercase tracking-wide"
									>
										<span>{f.label}:</span>
										<span className="font-bold">{f.value}</span>
										<button
											type="button"
											onClick={() => handleFilterBy({ type: f.key, value: "" })}
											className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}

								<Button
									variant="destructive-outline"
									size="sm"
									className="rounded-full"
									onClick={handleResetAllFilters}
								>
									{tpf("reset_all")}
								</Button>
							</div>
						)}

						<div className="flex flex-col justify-end gap-2 mt-4 md:flex-row">
							<Button
								variant="ghost"
								size="sm"
								onClick={handleToggleFilter}
								className="w-full md:w-fit"
							>
								{tpf("close")}
							</Button>
							<Button
								variant="ghost-destructive"
								size="sm"
								onClick={handleResetAllFilters}
								className="w-full md:w-fit"
							>
								{tpf("reset_all")}
							</Button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</section>
	);
});
export default ToolbarTable;
